import { createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import assign from 'lodash/assign';
import set from 'lodash/set';
import { pender } from 'redux-pender';
import { postEscrowCertificationNumber, postEscrowCertificationPhone, getEscrowCompletionInfo, postEscrowApplication } from 'app/api';
import { ESCROW_NOMINEE_TYPE } from 'app/constants/escrow';

const CHANGE_STATE = 'escrow.application/CHANGE_STATE';
const changeState = createAction(CHANGE_STATE);

const POST_CERTIFICATION_PHONE = 'escrow.application/POST_CERTIFICATION_PHONE';
const postCertificationPhone = createAction(POST_CERTIFICATION_PHONE, postEscrowCertificationPhone);

const POST_CERTIFICATION_NUMBER = 'escrow.application/POST_CERTIFICATION_NUMBER';
const postCertificationNumber = createAction(POST_CERTIFICATION_NUMBER, postEscrowCertificationNumber);

const POST_APPLICATION = 'escrow.application/POST_POST_APPLICATION';
const postApplication = createAction(POST_APPLICATION, postEscrowApplication);

const GET_COMPLETION_INFO = 'escrow.application/GET_COMPLETION_INFO';
const getCompletionInfo = createAction(GET_COMPLETION_INFO, getEscrowCompletionInfo);

const initialState = {
	ui: {
		isApplicationFormInit: false,
		isLoading: false,
		isJointNominees: false,
		isSameAccount: false,
		isSameApplicant: false,
		isSameBusinessEmail: false,
		isSameBusinessAddressAsPostalAddress: false,
		isSameCertifiedAddressAsPostalAddress: false,
		isSamePostalAddressAsBusinessAddress: false,
		homeserviceTerms: {
			personalInfo: false,
			personalOptionInfo: false,
			personalOffer: false,
			personalProcess: false,
			sevenDaysRefund: false,
		},
		warrantyTerms: {
			personalInfo: false,
			personalProvision: false,
			useAndRefund: false,
		},
		stepList: ['차량정보확인', '결제방법 선택', '신청주문서'],
	},
	applicationInfo: {
		userId: '',
		name: '',
		phone: '',
		email: '',
		refundAccount: {
			bank: '없음',
			name: '',
			number: '',
		},
		extendedWarrantyMonth: null,
		certificationKey: '',
		paymentMethods: null,
		applicantType: 'PERSONAL',
		applicationMethod: 'MOBILE',
		consultationTimeType: 'A_EARLY_MORNING',
		nominees: [
			{
				name: '',
				applicantType: 'PERSONAL',
				nomineeType: ESCROW_NOMINEE_TYPE.MAIN_APPLICANT_NOMINEE,
				phone: '',
				email: '',
				birth: '',
				gender: '',
				invoice: {
					email: '',
				},
				ownershipRate: 100,
				postalAddress: {
					buildingCd: '',
					detail: '',
					zipCd: '',
					addressDetail: '',
				},
				certifiedAddress: {
					buildingCd: '',
					detail: '',
					zipCd: '',
					addressDetail: '',
				},
				businessName: '',
				businessNumber: '',
				businessAddress: {
					buildingCd: '',
					detail: '',
					zipCd: '',
					addressDetail: '',
				},
			},
			{
				name: '',
				applicantType: '',
				nomineeType: ESCROW_NOMINEE_TYPE.SUB_APPLICANT_NOMINEE,
				phone: '',
				ownershipRate: 0,
				certifiedAddress: {
					buildingCd: '',
					detail: '',
					zipCd: '',
					addressDetail: '',
				},
			},
		],
	},
	certificationInfo: {
		key: '',
		phone: '',
		certificationNumber: '',
		isCertified: false,
	},
	completionInfo: {},
	validation: {
		name: {
			isError: false,
			errorText: '',
		},
		phone: {
			isError: false,
			errorText: '',
		},
		email: {
			isError: false,
			errorText: '',
		},
		certificationNumber: {
			isError: false,
			errorText: '',
		},
		refundAccount: {
			bank: {
				isError: false,
				errorText: '',
			},
			name: {
				isError: false,
				errorText: '',
			},
			number: {
				isError: false,
				errorText: '',
			},
		},
		nominees: [
			{
				name: {
					isError: false,
					errorText: '',
				},
				birth: {
					isError: false,
					errorText: '',
				},
				phone: {
					isError: false,
					errorText: '',
				},
				email: {
					isError: false,
					errorText: '',
				},
				postalAddress: {
					isError: false,
					errorText: '',
				},
				certifiedAddress: {
					isError: false,
					errorText: '',
				},
				businessAddress: {
					isError: false,
					errorText: '',
				},
				invoice: {
					email: {
						isError: false,
						errorText: '',
					},
				},
				ownershipRate: {
					isError: false,
					errorText: '',
				},
				businessName: {
					isError: false,
					errorText: '',
				},
				businessNumber: {
					isError: false,
					errorText: '',
				},
			},
			{
				name: {
					isError: false,
					errorText: '',
				},
				phone: {
					isError: false,
					errorText: '',
				},
				ownershipRate: {
					isError: false,
					errorText: '',
				},
				certifiedAddress: {
					isError: false,
					errorText: '',
				},
			},
		],
	},
};

export const escrowApplicationActions = {
	changeState,
	postCertificationPhone,
	postCertificationNumber,
	postApplication,
	getCompletionInfo,
};

export default handleActions(
	{
		[CHANGE_STATE]: (state, action) =>
			produce(state, (draft) => {
				try {
					const { name, values } = action.payload;

					const root = draft[name];

					if (!root) {
						throw Error(`initialState 에 정의되지 않은 프로퍼티 이름(${name}) 입니다.`);
					}

					Object.keys(values).forEach((key) => {
						set(root, key, values[key]);
					});

					assign(draft, { [name]: root });
				} catch (error) {
					console.error(error);
				}
			}),
		...pender({
			type: POST_CERTIFICATION_PHONE,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					const { key } = data;

					draft.certificationInfo.key = key;
					draft.applicationInfo.certificationKey = key;
				}),
		}),
		...pender({
			type: POST_CERTIFICATION_NUMBER,
			onSuccess: (state) =>
				produce(state, (draft) => {
					draft.certificationInfo.isCertified = true;
					draft.validation.certificationNumber = {
						isError: false,
						errorText: '',
					};
				}),
		}),
		...pender({
			type: POST_APPLICATION,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;

					draft.completionInfo = data;
				}),
		}),
		...pender({
			type: GET_COMPLETION_INFO,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;

					draft.completionInfo = data;
				}),
		}),
	},
	initialState,
);
